/** @type {readonly {field: keyof import('./parseExport').NestiFyzRulebook, title: string}[]} */
export const NESTIFYZ_RULEBOOK_CONTENT_SECTIONS = [
  { field: "introduction", title: "Introduction" },
  { field: "goal", title: "Goal" },
  { field: "setup", title: "Setup" },
  { field: "gameplay", title: "Gameplay" },
  { field: "endOfGame", title: "End of Game" },
  { field: "extraRules", title: "Extra Rules" },
  { field: "appendix", title: "Appendix" },
  { field: "faq", title: "FAQ" },
  { field: "credits", title: "Credits" },
  { field: "changelog", title: "Changelog" },
];

/**
 * @param {import('./parseExport').NestiFyzRulebook} rulebook
 * @returns {string|undefined}
 */
export const getRulebookExportName = (rulebook) => {
  if (rulebook.versionName) {
    return rulebook.versionName;
  }

  const documentName = rulebook.documentFile?.name || rulebook.document;
  if (!documentName) {
    return undefined;
  }

  return documentName
    .split("/")
    .pop()
    ?.replace(/\.[^./]+$/, "");
};

/**
 * @param {import('./parseExport').NestiFyzRulebook} rulebook
 * @returns {number}
 */
const getRulebookTimestamp = (rulebook) => {
  const parsed = Date.parse(rulebook.createdAt || rulebook.updatedAt || "");
  return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
};

/**
 * @param {import('./parseExport').NestiFyzRulebook} rulebook
 * @returns {boolean}
 */
export const hasImportableRulebookContent = (rulebook) => {
  return NESTIFYZ_RULEBOOK_CONTENT_SECTIONS.some(({ field }) => {
    const value = rulebook[field];
    return typeof value === "string" && value.trim().length > 0;
  });
};

/**
 * @param {import('./parseExport').NestiFyzRulebook} rulebook
 * @param {number} index
 * @returns {string}
 */
export const getRulebookIllustrationName = (rulebook, index) => {
  return `${getRulebookProgressLabel(rulebook)} - Illustration ${index + 1}`;
};

/**
 * @param {File} file
 * @param {import('./parseExport').NestiFyzRulebook} rulebook
 * @param {number} index
 * @returns {File}
 */
export const buildRulebookIllustrationFile = (file, rulebook, index) => {
  const extension = file.name.includes(".") ? file.name.slice(file.name.lastIndexOf(".")) : "";
  const displayName = `${getRulebookIllustrationName(rulebook, index)}${extension}`;

  return new File([file], displayName, { type: file.type, lastModified: file.lastModified });
};

/**
 * @param {import('./parseExport').NestiFyzRulebook} rulebook
 * @returns {File[]}
 */
export const getRulebookIllustrationFiles = (rulebook) => {
  return rulebook.illustrationFiles || [];
};

/**
 * @param {import('./parseExport').NestiFyzRulebook} rulebook
 * @returns {boolean}
 */
export const hasImportableRulebookIllustrations = (rulebook) => {
  return getRulebookIllustrationFiles(rulebook).length > 0;
};

/**
 * @param {import('./parseExport').NestiFyzRulebook[]} [rulebooks]
 * @returns {import('./parseExport').NestiFyzRulebook[]}
 */
export const getRulebooksForImport = (rulebooks = []) => {
  return [...rulebooks]
    .filter(
      (rulebook) =>
        rulebook?.documentFile || hasImportableRulebookContent(rulebook) || hasImportableRulebookIllustrations(rulebook)
    )
    .sort((a, b) => getRulebookTimestamp(a) - getRulebookTimestamp(b));
};

/**
 * @param {import('./parseExport').NestiFyzRulebook[]} [rulebooks]
 * @returns {import('./parseExport').NestiFyzRulebook[]}
 */
export const getImportableRulebooks = (rulebooks = []) => {
  return getRulebooksForImport(rulebooks).filter((rulebook) => rulebook.documentFile);
};

/**
 * @param {import('./parseExport').NestiFyzRulebook} rulebook
 * @returns {string}
 */
export const getRulebookProgressLabel = (rulebook) => {
  return getRulebookExportName(rulebook) || rulebook.documentFile?.name || rulebook.document || "Rulebook";
};

/**
 * @param {import('./parseExport').NestiFyzRulebook} rulebook
 * @returns {string}
 */
export const buildNestiFyzRulebookTopicBody = (rulebook) => {
  const sections = [];

  for (const { field, title } of NESTIFYZ_RULEBOOK_CONTENT_SECTIONS) {
    const value = rulebook[field];

    if (typeof value !== "string" || value.trim().length === 0) {
      continue;
    }

    sections.push(`## ${title}\n\n${value.trim()}`);
  }

  if (rulebook.id) {
    sections.push(`Nestifyz Rulebook Id: ${rulebook.id}`);
  }

  return sections.join("\n\n");
};

/**
 * @param {Array<{name: string, type: string, archived: boolean}>} topics
 * @param {import('./parseExport').NestiFyzRulebook} rulebook
 * @returns {{name: string, type: string, archived: boolean}|undefined}
 */
export const findExistingImportedRulebookTopic = (topics, rulebook) => {
  const name = getRulebookExportName(rulebook);

  if (!name) {
    return undefined;
  }

  return topics.find((topic) => topic.name === name && topic.type === "Rules" && !topic.archived);
};
